<template>
  <div class="flex flex-col w-full h-full overflow-y-auto pb-6 gap-6">
    <!-- Bundle packs section -->
    <section class="flex flex-col w-full gap-3">
      <div class="flex items-center justify-between">
        <span class="font-semibold text-lg">כרטיסיות</span>
        <Button
          label="כרטיסייה חדשה"
          icon="pi pi-plus"
          size="small"
          outlined
          @click="openAssignPack"
        />
      </div>

      <div v-if="loadingPacks" class="flex justify-center w-full p-4">
        <loader />
      </div>
      <template v-else>
        <div v-if="activePacks.length === 0 && archivedPacks.length === 0" class="text-sm text-gray-500 p-2">
          ללקוח/ה אין כרטיסיות עדיין
        </div>

        <div v-if="activePacks.length > 0" class="flex flex-col gap-2">
          <span class="text-sm text-gray-600">פעילות</span>
          <div
            v-for="pack of activePacks"
            :key="pack._id"
            v-wave
            class="flex items-center justify-between border rounded-md p-3 bg-white cursor-pointer hover:bg-gray-50"
            @click="openPackDetails(pack)"
          >
            <div class="flex items-center gap-3 min-w-0">
              <div class="bg-primary-200 rounded-full w-10 h-10 flex items-center justify-center flex-none">
                <i class="pi pi-id-card text-primary-700" />
              </div>
              <div class="flex flex-col min-w-0">
                <span class="font-medium truncate">{{ pack.templateName || 'כרטיסייה' }}</span>
                <span class="text-xs text-gray-600">
                  {{ pack.remainingUses }} מתוך {{ pack.totalUses }} מימושים
                  <template v-if="pack.expiresAt">
                    · בתוקף עד {{ formatDate(pack.expiresAt) }}
                  </template>
                </span>
                <span v-if="pack.status === BundlePackStatus.PendingPayment" class="text-xs text-orange-600 mt-1">
                  ממתינה לתשלום
                </span>
              </div>
            </div>
            <i class="pi pi-angle-left text-gray-400" />
          </div>
        </div>

        <div v-if="archivedPacks.length > 0" class="flex flex-col gap-2 mt-2">
          <span class="text-sm text-gray-600">לא פעילות</span>
          <div
            v-for="pack of archivedPacks"
            :key="pack._id"
            class="flex items-center justify-between border rounded-md p-3 bg-gray-50 opacity-70"
          >
            <div class="flex items-center gap-3 min-w-0">
              <div class="bg-gray-200 rounded-full w-10 h-10 flex items-center justify-center flex-none">
                <i class="pi pi-id-card text-gray-600" />
              </div>
              <div class="flex flex-col min-w-0">
                <span class="font-medium truncate">{{ pack.templateName || 'כרטיסייה' }}</span>
                <span class="text-xs text-gray-600">{{ statusLabel(pack.status) }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </section>

    <!-- Vouchers section -->
    <section class="flex flex-col w-full gap-3">
      <div class="flex items-center justify-between">
        <span class="font-semibold text-lg">שוברים</span>
        <Button
          label="שובר חדש"
          icon="pi pi-plus"
          size="small"
          outlined
          @click="openNewVoucher"
        />
      </div>

      <div v-if="loadingVouchers" class="flex justify-center w-full p-4">
        <loader />
      </div>
      <template v-else>
        <div v-if="customerVouchers.length === 0" class="text-sm text-gray-500 p-2">
          אין שוברים ללקוח/ה זה
        </div>

        <div v-if="activeVouchers.length > 0" class="flex flex-col gap-2">
          <span class="text-sm text-gray-600">פעילים</span>
          <div class="flex flex-col">
            <voucher-row
              v-for="voucher of activeVouchers"
              :key="voucher._id"
              :voucher="voucher"
              @click="openVoucherDetails(voucher)"
            />
          </div>
        </div>

        <div v-if="inactiveVouchers.length > 0" class="flex flex-col gap-2 mt-2">
          <span class="text-sm text-gray-600">לא פעילים</span>
          <div class="flex flex-col">
            <voucher-row
              v-for="voucher of inactiveVouchers"
              :key="voucher._id"
              :voucher="voucher"
              @click="openVoucherDetails(voucher)"
            />
          </div>
        </div>
      </template>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, PropType, ref } from 'vue';
import { DateTime } from 'luxon';
import {
  BundlePackStatus,
  ICustomer,
  ICustomerBundlePack,
  IVoucher
} from '@torim-app/types';
import { BundlePacksApi } from '@app/api/admin/bundle-packs-api';
import { PromotionsApi } from '@app/api/admin/promotions-api';
import { useDrawer } from '@torim-app/shared-components';
import VoucherRow from '@app/components/admin/components/VoucherRow.vue';
import VoucherDrawer from '@app/components/admin/components/VoucherDrawer.vue';
import IssuedBundlePackDrawer from '@app/components/admin/components/IssuedBundlePackDrawer.vue';
import NewVoucher from '@app/components/admin/modals/NewVoucher.vue';
import AssignBundlePack from '@app/components/admin/modals/AssignBundlePack.vue';
import { trackEvent } from '@app/composables/posthog';

const props = defineProps({
  customer: {
    type: Object as PropType<ICustomer>,
    required: true
  }
});

const { openDrawer, closeDrawer } = useDrawer();

const loadingPacks = ref(false);
const loadingVouchers = ref(false);
const packs = ref<ICustomerBundlePack[]>([]);
const allVouchers = ref<Array<IVoucher & { isPaid: boolean }>>([]);

// Backend already scopes these to the customer (getVouchersForCustomer).
const customerVouchers = computed(() => allVouchers.value);

// A voucher is inactive once it's been used or has passed its validity date
// (mirrors the status chip in VoucherRow: מומש / פג תוקף vs בתוקף).
const isVoucherExpired = (voucher: IVoucher) =>
  DateTime.fromISO(typeof voucher.validUntilDate === 'string' ? voucher.validUntilDate : voucher.validUntilDate.toISOString())
    < DateTime.now();

const activeVouchers = computed(() =>
  customerVouchers.value.filter(v => !v.used && !isVoucherExpired(v))
);

const inactiveVouchers = computed(() =>
  customerVouchers.value.filter(v => v.used || isVoucherExpired(v))
);

const activePacks = computed(() =>
  packs.value.filter(p =>
    p.status === BundlePackStatus.Active || p.status === BundlePackStatus.PendingPayment
  )
);

const archivedPacks = computed(() =>
  packs.value.filter(p =>
    p.status === BundlePackStatus.Exhausted ||
    p.status === BundlePackStatus.Expired ||
    p.status === BundlePackStatus.Cancelled
  )
);

const formatDate = (value: Date | string) =>
  DateTime.fromISO(typeof value === 'string' ? value : value.toISOString())
    .toLocaleString(DateTime.DATE_SHORT, { locale: 'he-IL' });

const statusLabel = (status: BundlePackStatus) => {
  switch (status) {
    case BundlePackStatus.Exhausted: return 'מומשה במלואה';
    case BundlePackStatus.Expired: return 'פג תוקף';
    case BundlePackStatus.Cancelled: return 'בוטלה';
    default: return '';
  }
};

const fetchPacks = async () => {
  loadingPacks.value = true;
  try {
    packs.value = await BundlePacksApi.fetchCustomerPacks(props.customer.id);
  } catch (e) {
    console.error(e);
  } finally {
    loadingPacks.value = false;
  }
};

const fetchVouchers = async () => {
  loadingVouchers.value = true;
  try {
    allVouchers.value = await PromotionsApi.fetchCustomerVouchers(props.customer.id);
  } catch (e) {
    console.error(e);
  } finally {
    loadingVouchers.value = false;
  }
};

const openAssignPack = () => {
  trackEvent('bundle_pack_assign_clicked');
  openDrawer(
    AssignBundlePack,
    {
      customer: props.customer,
      onAssigned: async () => {
        await fetchPacks();
      },
      onClose: closeDrawer
    },
    { title: 'שיוך כרטיסייה' }
  );
};

const openPackDetails = (pack: ICustomerBundlePack) => {
  trackEvent('bundle_pack_details_viewed', { packId: pack._id });
  openDrawer(
    IssuedBundlePackDrawer,
    {
      pack: { ...pack, customerName: props.customer.fullName, customerPhone: props.customer.phoneNumber },
      onChanged: async () => { await fetchPacks(); },
      onClose: closeDrawer
    },
    { title: 'פרטי כרטיסייה', closable: true }
  );
};

const openNewVoucher = () => {
  trackEvent('voucher_create_clicked', { from: 'customer-profile' });
  openDrawer(
    NewVoucher,
    {
      customer: props.customer,
      onCreatedVoucher: async () => {
        await fetchVouchers();
        closeDrawer();
      },
      onClose: closeDrawer
    },
    { title: 'יצירת שובר חדש' }
  );
};

const openVoucherDetails = (voucher: IVoucher & { isPaid: boolean }) => {
  openDrawer(
    VoucherDrawer,
    {
      selectedVoucher: voucher,
      onFetchVouchers: async () => {
        await fetchVouchers();
      }
    },
    { title: 'פרטי שובר', closable: true }
  );
};

onMounted(() => {
  fetchPacks();
  fetchVouchers();
});
</script>
